import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  addDoc,
  serverTimestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../lib/firebase/app';
import { tryRefreshAuth, markAuthHealthy } from '../lib/firebase/auth';
import { coordsToFrequency } from '../lib/frequency';
import type { PublicMessage } from '../types/message';

const MESSAGES_COLLECTION = 'messages';
const MESSAGE_LIMIT = 100;
const CACHE_KEY = 'ourhertz_msg_cache';

interface CachedMessage {
  id: string;
  text: string;
  senderId: string;
  senderNickname: string;
  gender: 'male' | 'female' | 'unknown';
  profileImageUrl?: string;
  lat: number;
  lng: number;
  radius: number;
  isBot: boolean;
  isNearbyNotification: boolean;
  isDropNotification?: boolean;
  manualFreqApplied: boolean;
  timestampMs: number; // stored as ms for reliable serialization
}

/** Load cached messages from localStorage */
export function loadCachedMessages(): CachedMessage[] {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch { return []; }
}

/** Save messages to localStorage cache (convert timestamps to ms) */
function appendToCache(msgs: PublicMessage[]) {
  try {
    const existing = loadCachedMessages();
    const idSet = new Set(existing.map(m => m.id));
    for (const m of msgs) {
      if (idSet.has(m.id)) continue;
      const tsMs = m.timestamp?.toMillis?.() ?? Date.now();
      existing.push({
        id: m.id, text: m.text, senderId: m.senderId,
        senderNickname: m.senderNickname, gender: m.gender,
        profileImageUrl: m.profileImageUrl,
        lat: m.lat, lng: m.lng, radius: m.radius,
        isBot: m.isBot, isNearbyNotification: m.isNearbyNotification,
        isDropNotification: m.isDropNotification,
        manualFreqApplied: m.manualFreqApplied,
        timestampMs: tsMs,
      });
      idSet.add(m.id);
    }
    // Keep last 300 messages
    const trimmed = existing.sort((a, b) => a.timestampMs - b.timestampMs).slice(-300);
    localStorage.setItem(CACHE_KEY, JSON.stringify(trimmed));
  } catch { /* storage full */ }
}

/** Clear message cache */
export function clearMessageCache() {
  localStorage.removeItem(CACHE_KEY);
}

/**
 * Subscribe to real-time message stream.
 * Loads cached messages immediately, then merges new messages from Firestore.
 * Only NEW messages (not in cache) trigger battery charging.
 */
/**
 * Subscribe to real-time message stream. Auto-retries on permission-denied.
 */
export function subscribeMessages(
  onUpdate: (messages: PublicMessage[], newMsgIds: Set<string>, fromServer: boolean) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const cachedIds = new Set(loadCachedMessages().map(m => m.id));
  const sessionStartTime = Date.now();
  let innerUnsub: Unsubscribe | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let cancelled = false;
  let retryCount = 0;

  function start() {
    const q = query(
      collection(db, MESSAGES_COLLECTION),
      orderBy('timestamp', 'desc'),
      limit(MESSAGE_LIMIT),
    );

    innerUnsub = onSnapshot(
      q,
      (snapshot) => {
        retryCount = 0;
        if (!snapshot.metadata.fromCache) markAuthHealthy();
        const firestoreMsgs: PublicMessage[] = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() } as PublicMessage))
          .reverse(); // oldest first

        // Determine which messages are genuinely new (not from cache, arrived after session start).
        // Grace window is generous (5 minutes) to tolerate device clock skew: sessionStartTime
        // is Date.now() on the client, while tsMs is server time. A device clock 2 minutes
        // ahead of server would otherwise classify legitimate live messages as pre-session and
        // block them via the downstream filter.
        const newMsgIds = new Set<string>();
        const NEW_MSG_GRACE_MS = 5 * 60 * 1000;
        for (const m of firestoreMsgs) {
          if (!cachedIds.has(m.id)) {
            const tsMs = m.timestamp?.toMillis?.() ?? 0;
            if (tsMs >= sessionStartTime - NEW_MSG_GRACE_MS) {
              newMsgIds.add(m.id);
            }
            cachedIds.add(m.id);
          }
        }

        // Save ONLY session-fresh messages to the persistent `ourhertz_msg_cache` store.
        // Pre-session messages are intentionally excluded: they must flow through the
        // gap-fill popup (cached via `cacheGapMessages` after user confirms), not be
        // silently persisted here. Previously caching everything corrupted the baseline
        // so the next session's gap-fill query cutoff advanced past legitimate missed
        // messages and the popup returned 0.
        if (newMsgIds.size > 0) {
          const sessionFresh = firestoreMsgs.filter(m => newMsgIds.has(m.id));
          appendToCache(sessionFresh);
        }

        onUpdate(firestoreMsgs, newMsgIds, !snapshot.metadata.fromCache);
      },
      (err) => {
        console.error('[MessageService] Subscribe error:', err);
        onError?.(err);
        const code = (err as { code?: string }).code;
        if (!cancelled && retryCount < 5 && (code === 'permission-denied' || code === 'unauthenticated')) {
          const delay = Math.min(3000 * Math.pow(2, retryCount), 30000);
          retryCount++;
          retryTimer = setTimeout(async () => {
            if (cancelled) return;
            const refreshed = await tryRefreshAuth();
            if (!cancelled && refreshed) start();
          }, delay);
        }
      },
    );
  }

  start();

  return () => {
    cancelled = true;
    if (retryTimer) clearTimeout(retryTimer);
    innerUnsub?.();
  };
}

export interface GapFillResult {
  messages: PublicMessage[];
  totalCharge: number;
  /** 모든 무료 메시지 합계 (블러 + 1km 이내 + 접속알림/드롭) — 하위호환 */
  freeCount: number;
  chargedCount: number;
  blurredCount: number;
  /** 접속알림 + 위치드롭 (거리 무관 무료) */
  notificationCount: number;
  oldestTimestamp: number;
  newestTimestamp: number;
}

/**
 * Fill gap: load messages between last cached timestamp and now.
 * Returns the messages and a detailed cost breakdown.
 * Does NOT cache messages — caller must call `cacheGapMessages()` after user confirms.
 */
export async function fillMessageGap(
  lastCachedTimestampMs: number,
  maxHours: number,
  myLat: number,
  myLng: number,
  batteryTiers: { radius: number; cost: number }[],
  myUid: string,
  /** IDs already in processedMsgCacheRef — messages that were received live in a
   *  prior session (or skipped/cut) and must not be re-surfaced. Closes the gap
   *  where the throttled watermark update lags by up to 60s and leaves recent
   *  live messages re-queryable. */
  alreadyProcessedIds: Set<string>,
  lastMessageReceivedAt?: number,
  radarRadiusKm?: number,
  receptionMode?: 'blur_beyond' | 'sensitivity_only',
  messageReceptionRadiusKm?: number,
  /** User turned AI bots off — bot messages must not be surfaced (nor charged for). */
  rejectBot?: boolean,
): Promise<GapFillResult> {
  const empty: GapFillResult = { messages: [], totalCharge: 0, freeCount: 0, chargedCount: 0, blurredCount: 0, notificationCount: 0, oldestTimestamp: 0, newestTimestamp: 0 };
  if (maxHours <= 0) return empty;

  const { query: q2, collection: col, where: w, orderBy: ob, limit: lim, getDocs, Timestamp } = await import('firebase/firestore');
  const { calcDistance } = await import('../lib/distance');

  // Cutoff is the time window ONLY — `alreadyProcessedIds` is the authoritative dedup.
  // Previously we also used `Math.max` against `lastCachedTimestampMs` and `watermark`,
  // but those baselines get contaminated by older bugs that cached / watermarked
  // pre-session messages that the display layer had filtered out. Once contaminated,
  // the query's cutoff moves past legitimate missed messages and `fillMessageGap`
  // returns 0 even when a popup should appear. Relying on `alreadyProcessedIds` (built
  // from `processedMsgCacheRef`, which only records messages the client genuinely
  // accepted) gives a robust dedup that survives past contamination.
  // `lastCachedTimestampMs` and `lastMessageReceivedAt` are kept in the signature for
  // callers / future use but no longer affect the query window.
  void lastCachedTimestampMs;
  void lastMessageReceivedAt;
  const cutoff = Date.now() - maxHours * 3600000;
  const startTs = Timestamp.fromMillis(cutoff);

  // IMPORTANT: order DESC + reverse to ASC in code. `orderBy ASC + limit 200` returns
  // the OLDEST 200 within the window, so when global traffic exceeds 200 messages in
  // the lookback window the NEWEST (most recently-missed) messages are silently dropped
  // — which is exactly the opposite of what the popup needs. Ordering DESC gets the
  // newest 200 first; we then reverse for ASC processing downstream.
  const snap = await getDocs(q2(
    col(db, MESSAGES_COLLECTION),
    w('timestamp', '>', startTs),
    ob('timestamp', 'desc'),
    lim(200),
  ));

  const allMsgs = snap.docs
    .map(d => ({ id: d.id, ...d.data() } as PublicMessage))
    .reverse(); // oldest first for display/processing

  const radarMeters = (radarRadiusKm ?? 5) * 1000;
  const mode = receptionMode ?? 'blur_beyond';
  const receptionMeters = mode === 'sensitivity_only'
    ? radarMeters
    : (messageReceptionRadiusKm ?? 100) * 1000;

  const msgs: PublicMessage[] = [];
  let totalCharge = 0;
  let freeCount = 0;
  let chargedCount = 0;
  let blurredCount = 0;
  let notificationCount = 0;

  for (const m of allMsgs) {
    // Skip own messages — no reason to "receive" what the user sent
    if (m.senderId === myUid) continue;
    // Skip messages already processed (live-received in a prior session, or previously
    // skipped) — the Firestore-side watermark can lag behind due to throttling, and
    // without this filter those messages would be re-surfaced in the popup.
    if (alreadyProcessedIds.has(m.id)) continue;
    // AI bot messages are fully hidden when the user disabled bots — never surface
    // them in the missed-messages popup, and never charge cells for them.
    if (rejectBot && m.isBot) continue;

    const dist = calcDistance(myLat, myLng, m.lat, m.lng);
    const isFreeNotification = m.isNearbyNotification || m.isDropNotification;

    // Cutoff rules:
    // - Notifications/drops follow RADAR (so they don't clutter the chat from far
    //   users you never interact with — user explicitly complained about seeing
    //   34.9km notifications with a 10km radar).
    // - Regular messages follow RECEPTION (so blur_beyond mode still works).
    if (isFreeNotification) {
      if (dist > radarMeters) continue;
      msgs.push(m); freeCount++; notificationCount++; continue;
    }
    if (dist > receptionMeters) continue;

    msgs.push(m);

    // Blurred messages (beyond radar but within reception) are free
    const isBlurred = mode === 'blur_beyond' && dist > radarMeters;
    if (isBlurred) { blurredCount++; freeCount++; continue; }

    // Within radar — charge by distance tier
    const tier = batteryTiers.find(t => dist <= t.radius);
    const cost = tier?.cost ?? batteryTiers[batteryTiers.length - 1]?.cost ?? 0;
    if (cost > 0) { totalCharge += cost; chargedCount++; }
    else { freeCount++; }
  }

  const timestamps = msgs.map(m => m.timestamp?.toMillis?.() ?? 0).filter(t => t > 0);

  return {
    messages: msgs, totalCharge, freeCount, chargedCount, blurredCount, notificationCount,
    oldestTimestamp: timestamps.length > 0 ? Math.min(...timestamps) : 0,
    newestTimestamp: timestamps.length > 0 ? Math.max(...timestamps) : 0,
  };
}

/** Cache gap-fill messages in localStorage. Call after user confirms reception. */
export function cacheGapMessages(msgs: PublicMessage[]) {
  appendToCache(msgs);
}

/** Collect device/network/tracking metadata for message (Expo compatible) */
async function collectMessageMetadata() {
  const { Capacitor } = await import('@capacitor/core');
  const meta: Record<string, unknown> = {};

  // Device info
  const deviceInfo: Record<string, string> = {
    platform: Capacitor.getPlatform(),
  };
  if (Capacitor.isNativePlatform()) {
    try {
      const mod = await import(/* @vite-ignore */ '@capacitor/device');
      const info = await mod.Device.getInfo();
      deviceInfo.manufacturer = info.manufacturer;
      deviceInfo.modelName = info.model;
      deviceInfo.osName = info.operatingSystem;
      deviceInfo.osVersion = info.osVersion;
      deviceInfo.brand = info.manufacturer;
      deviceInfo.deviceName = info.name ?? info.model;
    } catch {}
  } else {
    deviceInfo.userAgent = navigator.userAgent;
  }
  meta.deviceInfo = deviceInfo;

  // Network info
  const networkInfo: Record<string, string> = { networkType: 'unknown' };
  if (Capacitor.isNativePlatform()) {
    try {
      const mod = await import(/* @vite-ignore */ '@capacitor/network');
      const status = await mod.Network.getStatus();
      networkInfo.networkType = status.connectionType;
    } catch {}
  } else {
    networkInfo.networkType = navigator.onLine ? 'online' : 'offline';
  }
  meta.networkInfo = networkInfo;

  // Tracking info
  meta.trackingInfo = {
    collectedAt: new Date().toISOString(),
    locale: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    userAgent: `${Capacitor.getPlatform()}/${(deviceInfo.osVersion ?? navigator.userAgent).substring(0, 20)}`,
  };

  return meta;
}

/**
 * Send a public message.
 */
export async function sendMessage(params: {
  text: string;
  senderId: string;
  senderNickname: string;
  gender: 'male' | 'female' | 'unknown';
  profileImageUrl?: string;
  lat: number;
  lng: number;
  radius: number;
  isBot?: boolean;
  isNearbyNotification?: boolean;
  manualFreqApplied?: boolean;
  /** Optional: sender's email for Expo-compatible tracking */
  senderEmail?: string;
  isGuest?: boolean;
}): Promise<string> {
  // Collect metadata in parallel (don't block send on failure)
  const metaPromise = collectMessageMetadata().catch(() => ({}));

  const frequency = coordsToFrequency(params.lat, params.lng);
  const messageUniqueId = `${params.isNearbyNotification ? 'nearby_notification' : 'msg'}_${params.senderId}_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;

  const meta = await metaPromise;

  const docRef = await addDoc(collection(db, MESSAGES_COLLECTION), {
    text: params.text,
    senderId: params.senderId,
    senderNickname: params.senderNickname,
    gender: params.gender,
    senderGender: params.gender,
    senderEmail: params.senderEmail ?? '',
    profileImageUrl: params.profileImageUrl ?? null,
    lat: params.lat,
    lng: params.lng,
    frequency,
    radius: params.radius,
    isBot: params.isBot ?? false,
    isGuest: params.isGuest ?? false,
    isNearbyNotification: params.isNearbyNotification ?? false,
    manualFreqApplied: params.manualFreqApplied ?? false,
    messageUniqueId,
    sentAt: serverTimestamp(),
    timestamp: serverTimestamp(),
    updatedAt: serverTimestamp(),
    ...meta,
  });

  return docRef.id;
}
